<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-home-experience-automation-panel-dynamic-chart">
    <div ref="panelRef" class="dynamic-wrapper">
      <div v-loading="controller.dynamicChartsLoading" ref="lineChartPanel" class="panel-wrapper">
        <div class="panel-header">{{ lineChartTitleMapper[selectedGaugePanel] }}</div>
        <div v-if="showLineChart" class="panel-body">
          <div class="panel-dropdown">
            <el-select ref="viewBySelect" v-model="selectedDateRange" collapse-tags size="mini" class="date-range-dropdown" popper-class="date-range-popper">
              <el-option
                v-for="item in dateRangeTypes"
                @click.native="handleLineChartViewBy(item.type, item.disabled)"
                :disabled="item.disabled"
                :key="item.type"
                :label="item.label"
                :value="item.type"
              >
              </el-option>
            </el-select>
          </div>
          <div ref="lineChart" class="dynamic-line-chart"></div>
        </div>
        <div v-else class="panel-body-no-data">
          <span class="content">No data available yet for this brand or time period. Please use the global filter to select a brand and time period with data available.</span>
        </div>
        <div v-if="showLineChart" class="panel-footer">
          <span class="footer-col">
            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="3" viewBox="0 0 21 3" fill="none">
              <path d="M0 1.76221H20" stroke="#080687" stroke-width="2" stroke-linecap="round" />
            </svg>
            {{ lineChartGraphMapper[lineChartTitleMapper[selectedGaugePanel]] }}
          </span>
          <span v-if="selectedGaugePanel !== 'volume' && showFooterTarget" class="footer-col">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="3" viewBox="0 0 22 3" fill="none">
              <path d="M1 1.76221H21" stroke="#19A5A8" stroke-width="2" stroke-linecap="round" stroke-dasharray="5 5" />
            </svg>
            Target
          </span>
        </div>
      </div>
      <div v-if="showBarChart" v-loading="controller.dynamicChartsLoading" ref="waterfallChartPanel" class="panel-wrapper">
        <div class="panel-header">Signals causing {{ waterfallChartFooterMapper[selectedGaugePanel] }}</div>
        <div v-if="barChartHasData" class="panel-body-waterfall">
          <div ref="waterfallChart"></div>
          <!-- <div style="display: flex; align-items: center; justify-content: center; gap: 10px; marginTop: 10px">
            <div style="display: flex; align-items: center; justify-content: center;">
              <div style="height: 10px; width: 1px; background-color: var(--site-font-tertiary-color); "></div>
              <div :style="waterfallHrStyle"></div>
            </div>
            <div style="fontWeight: 400: fontSize: 1em">{{ waterfallChartFooterMapper[selectedGaugePanel] }}</div>
            <div style="display: flex; align-items: center; justify-content: center;">
              <div :style="waterfallHrStyle"></div>
              <div style="height: 10px; width: 1px; background-color: var(--site-font-tertiary-color); "></div>
            </div>
          </div> -->
        </div>
        <div v-else class="panel-body-no-data">
          <span class="content">No data available yet for this brand or time period. Please use the global filter to select a brand and time period with data available.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { formatLabel } from './waterfall-chart-util';
import { mapGetters } from 'vuex';
import { formatDateRangeForPlotly, groupByDateRangeForVolume, initDateRangeRules, groupByDateRangeForBesBasAC } from './view-by-date-range-util';
import WyChartModule from '../../../../library/wy-chart-module';
import { AvailableGaugePanels, CONFIG_KEY_MAPPER } from './panel-chart-const';

export default Vue.extend({
  name: 'wy-home-experience-automation-panel-dynamic-chart',
  props: ['controller', 'selectedGaugePanel'],
  data() {
    return {
      wyChart: null,

      waterfallHrResizeTimeout: null,
      waterfallHrStyle: '0px',

      selectedDateRange: null,

      // both modified in handlePanelChange method in gauge-summary component
      showLineChart: true,
      showBarChart: true,
      barChartHasData: false,

      showFooterTarget: false,

      networkData: {
        [AvailableGaugePanels.bas]: null,
        [AvailableGaugePanels.automated_chat]: null,
        [AvailableGaugePanels.bes]: null,
        [AvailableGaugePanels.volume]: null,
      },

      lineChartGraphMapper: {
        'Bot Automation': 'BAS',
        'Cost per Automated Chat': '$/AC',
        'Bot Experience': 'BES',
        'Conversation Volume': 'Conversation Volume',
      },

      lineChartTitleMapper: {
        [AvailableGaugePanels.bas]: 'Bot Automation',
        [AvailableGaugePanels.automated_chat]: 'Cost per Automated Chat',
        [AvailableGaugePanels.bes]: 'Bot Experience',
        [AvailableGaugePanels.volume]: 'Conversation Volume',
      },
      waterfallChartFooterMapper: {
        [AvailableGaugePanels.bas]: 'Automation Gaps',
        [AvailableGaugePanels.bes]: 'Experience Gaps',
      },

      waterfallChartBarColors: ['#080687', '#0B38AB', '#2061D3', '#0B6CF0', '#6096DF', '#0AA1CC', '#19A5A8', '#14787A', '#598EAC'],

      dateRangeTypes: {
        year: {
          type: 'year',
          label: 'View by year',
          tickformat: '%Y',
          disabled: true,
          duraion: 99999,
          selected: this.selectedDateRange === 'year',
        },
        month: {
          type: 'month',
          label: 'View by month',
          tickformat: '%b %Y',
          disabled: true,
          duraion: 365,
          selected: this.selectedDateRange === 'month',
        },
        week: {
          type: 'week',
          label: 'View by week',
          tickformat: '%b %d %Y',
          disabled: true,
          duraion: 30,
          selected: this.selectedDateRange === 'week',
        },
        day: {
          type: 'day',
          label: 'View by day',
          tickformat: '%b %d %Y',
          disabled: true,
          duraion: 7,
          selected: this.selectedDateRange === 'day',
        },
      },
    };
  },

  computed: {
    ...mapGetters(['dataModel', 'companyConfig', 'config']),
  },

  methods: {
    handleLineChartViewBy(range, isDisabled) {
      if (isDisabled) return;

      this.selectedDateRange = this.dateRangeTypes[range].type;
      setTimeout(() => {
        this.plotLineChart();
      }, 10);
      const eventInfo = {
        metric_name: this.controller.metricsMap[this.controller.selectedGaugePanel] ? this.controller.metricsMap[this.controller.selectedGaugePanel] : this.controller.selectedGaugePanel,
        view_by_type: this.dateRangeTypes[this.selectedDateRange].label ? this.dateRangeTypes[this.selectedDateRange].label : this.selectedDateRange,
      };
      this.$router.logAmplitudeEvent({ eventName: 'Home_EA_EA: View By Selected', eventInfo });
    },

    resetNetworkData() {
      this.networkData = {
        [AvailableGaugePanels.bas]: null,
        [AvailableGaugePanels.automated_chat]: null,
        [AvailableGaugePanels.bes]: null,
        [AvailableGaugePanels.volume]: null,
      };

      this.selectedDateRange = null;
    },

    reloadBothPanels() {
      setTimeout(() => {
        // Internal timeout, plotly takes some time to render the chart
        this.plotLineChart();
        this.plotBarChart();
        this.controller.dynamicChartsLoading = false;
      }, 10);
    },

    handleWindowResize() {
      // Debounce the resize event to avoid multiple recalculations during rapid resizing
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.calculateWaterfallHrLine();
      }, 200); // Adjust the debounce time (in milliseconds) as per your needs
    },

    calculateWaterfallHrLine() {
      const chartContainerWidth = this.$refs.waterfallChart && this.$refs.waterfallChart.offsetWidth;
      const secondBarX = (chartContainerWidth / 8) * 2; // 8 bars in waterfall chart
      const seventhBarX = (chartContainerWidth / 8) * 7;
      const lineWidth = seventhBarX - secondBarX;

      this.waterfallHrStyle = {
        width: `${lineWidth / 2 - 100}px`,
        height: '1px',
        backgroundColor: 'var(--site-font-tertiary-color)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };
    },

    plotLineChart() {
      if (!this.selectedDateRange) {
        // make sure when global filter is changed, we set this.selectedDateRange to null
        const dateStartMoment = moment(this.dataModel.dateStart, 'YYYY-MM-DD');
        const dateEndMoment = moment(this.dataModel.dateEnd, 'YYYY-MM-DD');

        const dateRangeDatesDiff = dateEndMoment.diff(dateStartMoment, 'days', true) + 1;

        this.selectedDateRange = initDateRangeRules({
          dateRangeDatesDiff,
          dateRangeTypes: this.dateRangeTypes,
          companyConfigViewBy: (this.companyConfig.default || {}).selectedViewBy || 'week',
        });
      }

      const panelWrapperRefs = this.$refs.panelRef;
      if (!panelWrapperRefs) return;

      panelWrapperRefs.style.gridTemplateAreas = 'item item';
      panelWrapperRefs.style.gridTemplateColumns = '1fr 1fr';
      panelWrapperRefs.style.gridGap = '18px';

      const selectedData = this.networkData[this.selectedGaugePanel];

      const fullWidthMode = !this.showBarChart; // handled in gauge-summary.handlePanelChange logic

      const lineChartRefs = this.$refs.lineChart;
      const panelDivs = this.$refs.panelRef && this.$refs.panelRef.children && this.$refs.panelRef.children[0];

      if (fullWidthMode) {
        panelWrapperRefs.style.gridTemplateAreas = 'item';
        panelWrapperRefs.style.gridTemplateColumns = '1fr';
        panelWrapperRefs.style.gridGap = '0px';
      }
      lineChartRefs.style.display = 'block';
      lineChartRefs.style.justifyContent = 'unset';
      lineChartRefs.style.minWidth = 'unset';
      for (let i = 0; i < panelDivs.children.length; i++) {
        panelDivs.children[i].style.minWidth = 'unset';
      }

      const metrics = (selectedData && selectedData.dailyMetrics) || [];

      metrics.forEach((metric) => {
        if (metric.value == '-1') metric.value = null;
        if (metric && metric.rawData && metric.rawData.TotalBESScore == '-1') metric.rawData.TotalBESScore = null;
        if (metric && metric.rawData && metric.rawData.TotalBASScore == '-1') metric.rawData.TotalBASScore = null;
        if (metric && metric.rawData && metric.rawData.besScoreVolume == '-1') metric.rawData.besScoreVolume = null;
        if (metric && metric.rawData && metric.rawData.basScoreVolume == '-1') metric.rawData.basScoreVolume = null;

        this.wyChart.prepareGroupDates(metric, metric.date);
      });


      const wysdomContractValue = (this.config.api.configs.acConfig || {}).wysdomContractValue != null ? (this.config.api.configs.acConfig || {}).wysdomContractValue : -1;
      const customerResourceCosts = (this.config.api.configs.acConfig || {}).customerResourceCosts != null ? (this.config.api.configs.acConfig || {}).customerResourceCosts : -1;
      const thirdPartyBotPlatformFees = (this.config.api.configs.acConfig || {}).thirdPartyBotPlatformFees != null ? (this.config.api.configs.acConfig || {}).thirdPartyBotPlatformFees : -1;

      let groupedMetrics = null;
      if (this.selectedGaugePanel === AvailableGaugePanels.bas || this.selectedGaugePanel === AvailableGaugePanels.bes || this.selectedGaugePanel === AvailableGaugePanels.automated_chat) {
        groupedMetrics = groupByDateRangeForBesBasAC({
          selectedDateRange: this.selectedDateRange,
          metrics: metrics,
          selectedPanel: this.selectedGaugePanel,
          wysdomContractValue: wysdomContractValue,
          customerResourceCosts: customerResourceCosts,
          thirdPartyBotPlatformFees: thirdPartyBotPlatformFees,
        });
      } else {
        groupedMetrics = groupByDateRangeForVolume({ selectedDateRange: this.selectedDateRange, metrics: metrics });
      }

      const xVal = formatDateRangeForPlotly({ selectedDateRange: this.selectedDateRange, groupedMetrics: groupedMetrics, dateStart: metrics.length && metrics[0] && metrics[0].day });
      let yVal = groupedMetrics.map((metric) => metric.value);

      if (this.selectedGaugePanel === AvailableGaugePanels.bas) {
        yVal = yVal.map((val) => (val != null ? Number(val / 100) : null));
      }

      yVal = yVal.map((val) => {
        if (val != 0 && val == null) {
          val = '-1';
        }
        else if(val < 0.01) {
          val = 0;
        }
        else if(val == Number.POSITIVE_INFINITY) {
          val = '-1';
        }
        return val;
      });

      const hoverTemplate = yVal.map((val) => {
        if (val == '-1') {
          return `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper[this.selectedGaugePanel]] + '<br />N/A'}</b><extra></extra>`;
        }
        if (this.selectedGaugePanel === AvailableGaugePanels.volume) {
          return `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper[this.selectedGaugePanel]] + '<br />%{y:,}'}</b><extra></extra>`;
        }
        return `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper[this.selectedGaugePanel]] + '<br />%{y}'}</b><extra></extra>`;
      });

      let targetVal = null;
      this.showFooterTarget = true;
      const gaugePanelRef = this.controller.$refs.gaugeSummary;

      if (gaugePanelRef && gaugePanelRef.configBesBasValues) {
        const mappedKey = CONFIG_KEY_MAPPER[this.controller.selectedGaugePanel];

        if (gaugePanelRef.configBesBasValues && gaugePanelRef.configBesBasValues[mappedKey] && gaugePanelRef.configBesBasValues[mappedKey].maxValue != null)
          targetVal = gaugePanelRef.configBesBasValues[mappedKey].maxValue;
        else if (gaugePanelRef.configBesBasValues && gaugePanelRef.configBesBasValues[mappedKey] && gaugePanelRef.configBesBasValues[mappedKey].value != null) {
          targetVal = gaugePanelRef.configBesBasValues[mappedKey].value;
        }

        if (this.controller.selectedGaugePanel === AvailableGaugePanels.bas && targetVal != null) {
          targetVal = targetVal / 100;
        }
        if (this.selectedGaugePanel === AvailableGaugePanels.automated_chat) {
          targetVal = gaugePanelRef.configBesBasValues[mappedKey].minValue;
        }
      }

      this.showFooterTarget = targetVal != null;

      const lineTrace = {
        x: xVal, // xVal
        y: yVal,
        name: '',
        type: 'scatter',
        mode: 'lines+markers',
        cliponaxis: false,
        hoverinfo: 'x',

        marker: { color: xVal.length === 1 ? '#080687' : 'transparent' },
        line: {
          width: 1,
          color: '#080687',
        },
        hovertemplate: hoverTemplate,
        hoverlabel: {
          font: {
            size: 14,
            family: 'Helvetica Neue, Helvetica, arial, sans-serif',
          },
        },
        fill: 'none',
      };

      const horizontalLineTrace = {
        x: xVal, // xVal
        y: Array(xVal.length).fill(targetVal),
        name: 'target',
        type: 'scatter',
        mode: 'lines+markers',
        cliponaxis: false,
        marker: { color: xVal.length === 1 ? '19A5A8' : 'transparent' },
        line: {
          color: '19A5A8',
          width: 2,
          dash: 'dash',
        },
        hovertemplate: `<b>Target <br />%{y}</b><extra></extra>`,
        hoverlabel: {
          font: {
            size: 14,
            family: 'Helvetica Neue, Helvetica, arial, sans-serif',
          },
        },
        fill: 'none',
      };

      const traceData = [lineTrace, horizontalLineTrace];

      const tickFormat = this.dateRangeTypes[this.selectedDateRange].tickformat || '%b %d %Y';

      const layout = {
        ...({}),
        autosize: true,
        paper_bgcolor: '#f5f5f5',
        plot_bgcolor: '#f5f5f5',
        showlegend: false,
        hovermode: 'x',
        margin: {
          t: 8,
          pad: 8,
        },
        xaxis: {
          type: 'date',
          showgrid: false,
          zeroline: false,
          showline: true,
          tickformat: tickFormat,
          fixedrange: true,
          autorange: true,
          tickfont: {
            size: 12,
            family: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          },
          ticks: 'outside',
          tickcolor: 'white',
        },
        yaxis: {
          type: 'linear',
          side: 'right',
          showgrid: true,
          zeroline: false,
          rangemode: 'nonnegative',
          tickmode: 'array',
          autorange: true,
          automargin: true,
          fixedrange: true,
          tickprefix: this.selectedGaugePanel === AvailableGaugePanels.automated_chat ? '$' : null,
          tickfont: {
            size: 12,
            family: 'Helvetica Neue, Helvetica, Arial, sans-serif',
          },
        },
      };

      if (xVal.length > 1 && xVal.length < 5) {
        layout.xaxis.nticks = xVal.length;
        if (this.selectedDateRange === 'year') {
          layout.xaxis.tickformat = null;
        }
      }

      const viewingByYear = xVal.length == 1;
      const viewingByWeek = xVal.length == 5;
      const viewingByDays = xVal.length > 5;

      if (viewingByYear) {
        layout.xaxis.nticks = 3;
      }
      if (xVal.length < 5) {
        layout.xaxis.nticks = xVal.length;
      }

      if (viewingByDays) {
        layout.xaxis.tickmode = 'array';
        layout.xaxis.tickvals = [xVal[0], xVal[Math.round((xVal.length - 1) / 3)], xVal[Math.round((xVal.length - 1) / 3) * 2], xVal[xVal.length - 1]];
        layout.xaxis.nticks = 3;
      }

      if (viewingByWeek) {
        layout.xaxis.tickmode = 'array';
        layout.xaxis.tickvals = [xVal[0], xVal[Math.round((xVal.length - 1) / 2)], xVal[xVal.length - 1]];
        layout.xaxis.nticks = 3;
      }

      if ((this.selectedGaugePanel === AvailableGaugePanels.automated_chat || this.selectedGaugePanel === AvailableGaugePanels.volume) && fullWidthMode) {
        layout.margin = {
          ...layout.margin,
          t: 40,
          b: 80,
          l: 100,
          r: 100,
        };
      }

      // setting vertical boundary for y axis
      if (this.selectedGaugePanel === AvailableGaugePanels.bes) {
        const filteredYVal = yVal.filter(val => val != '-1')
        let minValue = targetVal != null && targetVal < Math.min(...filteredYVal) ? targetVal : Math.min(...filteredYVal);
        let maxValue = targetVal > Math.max(...filteredYVal) ? targetVal : Math.max(...filteredYVal);
        let diffValue = parseInt(maxValue - minValue);
        layout.yaxis.nticks = diffValue > 5 ? 5 : diffValue;
        let step = maxValue - minValue > 10 ? 10 : 1;
        const yAxisLayout = {
          // showgrid: true,
          range: [parseInt(minValue / 10) * 10 > 100 ? 100 : parseInt(minValue / 10) * 10, maxValue + step * 2],
          autorange: false,
          tickmode: null,
        };
        layout.yaxis = {
          ...layout.yaxis,
          ...yAxisLayout,
        };
      }

      if (this.selectedGaugePanel === AvailableGaugePanels.bas) {
        const filteredYVal = yVal.filter(val => val != '-1')
        let minValue = targetVal != null && targetVal < Math.min(...filteredYVal) ? targetVal : Math.min(...filteredYVal);
        let maxValue = targetVal > Math.max(...filteredYVal) ? targetVal : Math.max(...filteredYVal);
        let diffValue = parseInt((maxValue - minValue) * 100 + 1);

        layout.yaxis.nticks = diffValue > 5 ? 5 : diffValue;
        let step = maxValue - minValue > 0.1 ? 0.1 : 0.01;
        const yAxisLayout = {
          // showgrid: true,
          tickformat: '0.0%',
          range: [parseInt(minValue * 10) / 10, maxValue + step],
          autorange: false,
          tickmode: null,
        };
        layout.yaxis = {
          ...layout.yaxis,
          ...yAxisLayout,
        };
      }

      if (this.selectedGaugePanel === AvailableGaugePanels.automated_chat) {
        const filteredYVal = yVal.map((val) => {
          if (val == '-1') val = 0;
          return val;
        });
        let minValue = targetVal != null && targetVal < Math.min(...filteredYVal) ? targetVal : Math.min(...filteredYVal);
        let maxValue = targetVal > Math.max(...filteredYVal) ? targetVal : Math.max(...filteredYVal);
        let diffValue = parseInt((maxValue - minValue) * 100)/100 + 0.01;
        let step = parseInt((maxValue - minValue) * 100)/100 > 0.1 ? 0.1 : 0.01;
        const yAxisLayout = {
          range: [minValue, maxValue + step],
          autorange: false,
          tickmode: null,
        };
        layout.yaxis.nticks = diffValue * 100 > 6 ? 6 : diffValue * 100;
        if(layout.yaxis.nticks == 1) {
          layout.yaxis.nticks = 2
        }
        layout.yaxis = {
          ...layout.yaxis,
          tickformat: '.2f',
          ...yAxisLayout
        };
      }

      traceData[0].y.forEach((yData, index) => {
        if(yData == '-1') {
          traceData[0].y[index] = null;
        }
      });

      if (lineChartRefs) {
        // eslint-disable-next-line no-undef
        let pl = Plotly;
        pl.newPlot(lineChartRefs, traceData, layout, { responsive: true, displayModeBar: false });
        
        let targetNumber = 0;
        this.$refs.lineChart.on('plotly_hover', (data) => {
          try {
            data.points.forEach((point) => {
              let dataPoint = point; //data.points[0];
              if (dataPoint && dataPoint.data && dataPoint.data.marker) {
                let pn = dataPoint.pointNumber;
                let colors = [];
                xVal.forEach(() => {
                  colors.push('transparent');
                });
                colors[pn] = dataPoint.data.line.color;
                point.data.marker = { color: colors };
                // eslint-disable-next-line no-undef
                if(!point.data.hovertemplateTemp) {
                  point.data.hovertemplateTemp = point.data.hovertemplate;
                }
                let hasTargetValue = false;
                traceData[1].y.forEach(targetValue => {
                  if(targetValue != null) {
                    hasTargetValue = true;
                  }
                })
                if(hasTargetValue) {
                  if(point.data.name == 'target') {
                    targetNumber = point.pointNumber;
                  }
                  else {
                    if(targetNumber == point.pointNumber) {
                      if(point.data.hovertemplate == `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper[this.selectedGaugePanel]] + '<br />N/A'}</b><extra></extra>`) {
                        point.data.hovertemplate = point.data.hovertemplateTemp;
                      }
                    }
                    else {
                      point.data.hovertemplate = `<b>${this.lineChartGraphMapper[this.lineChartTitleMapper[this.selectedGaugePanel]] + '<br />N/A'}</b><extra></extra>`
                    }
                  }
                }
                pl.relayout(this.$refs.lineChart, point.data.marker, 0);
              }
            });
          } catch {
            // catch error
          }
        });

        // mouse out on point
        this.$refs.lineChart.on('plotly_unhover', (data) => {
          try {
            data.points.forEach((point) => {
              let dataPoint = point; //data.points[0];
              if (dataPoint && dataPoint.data && dataPoint.data.marker) {
                let colors = [];
                xVal.forEach(() => {
                  colors.push('transparent');
                });
                // console.log(dataPoint.data)
                point.data.marker = { color: colors };
                // eslint-disable-next-line no-undef
                Plotly.relayout(this.$refs.lineChart, point.data.marker, 0);
              }
            });
          } catch {
            // catch error
          }
        });

        const viewBySelectComponent = this.$refs.viewBySelect;
        if (this.showLineChart && viewBySelectComponent) {
          document.querySelector('.wy-container-main').addEventListener('scroll', () => {
            try {
              if (viewBySelectComponent.blur && viewBySelectComponent.blur()) {
                viewBySelectComponent.blur();
              }
            } catch (error) {
              // do nothing
            }
          });
        }
      }
    },

    plotBarChart() {
      const waterfallChartPanelRefs = this.$refs.waterfallChartPanel;
      if (!waterfallChartPanelRefs) return;

      waterfallChartPanelRefs.style.display = 'block';

      // handled in gauge-summary.handlePanelChange logic
      if (!(this.networkData[this.selectedGaugePanel] && this.networkData[this.selectedGaugePanel].barMetrics) || !this.barChartHasData) {
        this.barChartHasData = false;
        return;
      }

      const data = Object.entries(this.networkData[this.selectedGaugePanel].barMetrics).map(([label, value]) => ({ label: formatLabel(label), value: value }));
      data.sort((a, b) => b.value - a.value);

      const xVal = data.map((item) => item.label);
      const yVal = data.map((item) => item.value);

      const barTrace = {
        x: xVal,
        y: yVal,
        marker: {
          color: this.waterfallChartBarColors,
        },
        text: yVal.map((item) => {
          // if (item == 1) return '100';
          if (Number(item) <= 0.1 && Number(item) > 0) return '< 0.1';
          if (Number(item) == 0) return ''
          return Number(item).toFixed(1);
        }),
        textposition: 'outside',
        type: 'bar',
        hoverinfo: 'none',
      };

      var traceData = [barTrace];

      var layout = {
        barmode: 'stack',
        paper_bgcolor: 'rgba(245,246,249,1)',
        plot_bgcolor: 'rgba(245,246,249,1)',
        showlegend: false,
        margin: { pad: 8, t: 50 },
        xaxis: {
          fixedrange: true,
          tickfont: {
            size: 12,
            family: 'Helvetica',
          },
        },
        yaxis: {
          side: 'right',
          fixedrange: true,
          showticklabels: true,
          autorange: true,
          rangemode: 'tozero',
          tickfont: {
            size: 12,
            family: 'Helvetica',
          },
          title: {
            text: 'Impact',
            font: {
              size: 12,
              color: '#000000',
              family: 'Helvetica',
            },
          },
        },
      };

      const waterfallChartRefs = this.$refs.waterfallChart;
      if (waterfallChartRefs) {
        // eslint-disable-next-line no-undef
        Plotly.newPlot(waterfallChartRefs, traceData, layout, { responsive: true, displayModeBar: false });
        this.calculateWaterfallHrLine();
      }
    },
  },

  async created() {
    this.wyChart = new WyChartModule();
    window.addEventListener('resize', this.handleWindowResize);
  },

  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize);
  },
});
</script>

<style lang="scss">
@media (max-width: 1320px) {
  .wy-home-experience-automation-panel-dynamic-chart {
    .dynamic-wrapper {
      // make into 2 rows
      grid-template-areas: 'item' 'item' !important;
      grid-template-columns: 100% !important;
    }
    .panel-wrapper {
      min-width: 700px;
    }
  }
}

.wy-home-experience-automation-panel-dynamic-chart {
  margin-bottom: 20px;
  min-height: 300px;

  .js-plotly-plot .plotly .cursor-pointer {
    cursor: default !important;
  }

  .el-loading-mask {
    max-height: 100% !important;
  }

  .panel-header {
    padding: 18px 15px 18px 15px;
  }
}

.dynamic-wrapper {
  display: grid;
  grid-template-areas: 'item item';
  grid-template-columns: 1fr 1fr;
  grid-gap: 18px;
}

.panel-wrapper {
  border: 1px solid #cad2da;
  border-radius: 4px;
  background-color: white;
  overflow-x: auto;
  overflow-y: hidden;

  // hide scrollbar webkit
  &::-webkit-scrollbar {
    display: none;
  }
}

.panel-header {
  padding: 10px 16px 8px 16px;
  font-size: 20px;
  color: var(--site-font-chart-color);
  border-bottom: 1px solid #cad2da;
}

.panel-body-waterfall {
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-content: center;

  height: 100%;
  margin-top: -45px;
}

.panel-body {
  min-height: 400px;
  overflow: none;
}

.panel-body-no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;

  .content {
    max-width: 360px;
    font-weight: 500;
    line-height: 22px;
    font-size: 14px;
    text-align: center;
    text-align: center;
    color: #4b5158 !important;
  }
}
.date-range-popper {
  .el-select-dropdown__item.selected {
    color: #1a55ad !important;
    background-color: #e8f1ff !important;
    font-weight: 500;
  }
}
.panel-dropdown {
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;
  padding-right: 40px;
  padding-bottom: 30px;
  min-width: 400px;

  .el-select.date-range-dropdown.el-select--mini {
    width: 7.86vw !important;
    min-width: 140px;
    border: 1px #d9d9d9 solid;
    border-radius: 5px;
    position: relative;
    right: 0.24vw;
  }
  .el-select.date-range-dropdown.el-select--mini.blue-border {
    border: 1px #106fbc solid !important;
  }
  .el-select.date-range-dropdown.el-select--mini:active {
    border: 1px solid #1c4db2 !important;
  }

  input.el-input__inner {
    border-width: 0px 0px 1px 0px !important;
    border-radius: 5px;
    padding-left: 10px;
    font-size: 14px;
  }
  .el-input__inner {
    border: 1px solid transparent !important;
    &:hover {
      background: #f4f6f8;
    }
  }
}

.viewBy-dropdown {
  padding: 8px 12px;
  border-radius: 4px;
}
.viewBy-dropdown:hover {
  color: #409eff;
  border-color: #409eff;
}

.panel-footer {
  display: flex;
  align-items: center;
  justify-content: center !important;
  padding: 20px;
  border-top: 1px solid #cad2da;
  gap: 16px;
  font-weight: 400;

  .footer-col {
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.waterfall-footer {
  border-top: none;
  background-color: red;
}

// .exp-and-auto-table-section {
//   margin-bottom: 50px;
//   margin: 10px 0px 20px 0px;
//   width: unset;
//   min-height: 512px;
//   border: 1px solid #cad2da;
//   border-radius: 5px;
//   // height: 1175px;

//   .table-section {
//     border-radius: 6px;
//   }
//   .wy-table-list {
//     min-height: 449px;
//     width: calc(100% + 2px) !important;
//     position: relative;
//     background-color: #fff;
//     border: 0px solid #fff;
//     // height: 1110px;
//     // border-left: 1px solid #FFF;
//     // border-right: 1px solid #FFF;
//     left: -1px;
//   }

//   .el-loading-mask {
//     width: 100% !important;
//     border-top: 1px solid #cad2da;
//     // left: 1px;
//     // height: calc(100% - 2px) !important;
//     // height: 600px !important;
//   }
//   .header-info-poppers {
//     font-family: Helvetica Neue, Helvetica;
//     font-size: 14px;
//   }
//   .flex-container {
//     display: flex;
//     justify-content: space-evenly;
//     align-items: center;
//   }

//   .top-flex {
//     display: flex;
//     padding: 15px;
//     background-color: #fff;
//     padding-bottom: 4px;
//     // border-bottom: 1px solid #CAD2DA;
//     font-size: 18px;
//     justify-content: space-between;
//     margin-top: 3px;
//     height: 42px;
//     // border: 1px solid #CAD2DA;
//     // border-top-right-radius: 15px;
//     // border-top-left-radius: 15px;
//     // border-bottom: 1px solid transparent;
//   }
//   .bottom-left-stats-small-flex {
//     display: flex;
//     margin-top: 15px;
//     align-items: center;
//   }
//   .icon-info {
//     display: inline-block;
//     background: url('../../../../assets/icons/icon_info_performance_overview.svg') no-repeat;
//     width: 20px;
//     height: 17px;
//     position: relative;
//     top: 8px;
//     margin-left: 0.25vw;
//   }
//   .panel-title {
//     // font-size: 1.2vw;
//     font-size: 18px;
//     font-family: Helvetica Neue, Helvetica;
//     font-weight: 500;
//     color: var(--site-font-secondary-color);
//     display: inline-block;
//     position: relative;
//     top: 4px;
//   }
//   span.panel-type {
//     // padding: 8px;
//     font-size: 18px;
//     position: relative;
//     top: 4px;
//   }
//   .no-data-panel-type-container {
//     display: flex;
//     align-items: center;
//     margin-bottom: 16px;
//   }
//   .no-data-panel-type {
//     font-size: 18px;
//     position: relative;
//     top: 4px;
//     margin-left: 10px;
//   }

//   .el-input--mini .el-input__inner {
//     height: 31px;
//     width: 4.8vw;
//     line-height: 1.34vw;
//   }
//   .setting-arrow-down {
//     display: none;
//   }
//   .el-button.is-plain:hover,
//   .el-button.is-plain:active {
//     background: #fff;
//     border: none;
//     color: var(--site-font-tertiary-color);
//   }
//   .el-button {
//     font-family: 'Helvetica Neue', Helvetica !important;
//     border: none !important;
//     font-size: 14.22px;
//     color: var(--site-font-tertiary-color);
//     font-weight: normal;
//     margin-right: 0.5vw;
//   }
//   img.el-tooltip.move-info {
//     width: 30px;
//     position: relative;
//     cursor: move;
//     bottom: 6px;
//   }
//   .el-dropdown {
//     margin-right: 15px;
//   }
//   .wy-exp-and-auto-header-popup.right {
//     display: flex;
//     display: none;
//     // visibility: hidden;
//   }
//   .wy-exp-and-auto-header-popup.right.show {
//     display: flex !important;
//     // visibility: visible;
//   }
//   button.el-button.gear-dropdowns.el-button--default.is-plain.el-dropdown-selfdefine {
//     border: none !important;
//   }
//   .wy-table-list thead .wy-header {
//     display: none;
//   }
//   .wy-table-list .wy-table-main thead tr :first-child {
//     padding-left: 17px;
//   }
//   .wy-table-list thead tr th {
//     text-align: left;
//     color: var(--site-font-secondary-color);
//     padding: 25px 10px;
//     text-align: center;
//     font-size: 14.5px;
//     padding-left: 11px;
//     font-weight: 500;
//     background-color: #f5f8fa;
//   }
//   .wy-table-list .wy-table-wrapper {
//     position: relative;
//     border-top-left-radius: unset !important;
//     border-top-right-radius: unset !important;

//     .wy-page-nodata-with-available-date-range {
//       margin: 4%;
//       margin-top: 13%;
//     }
//   }
// }
</style>
